import { api } from "./api";
import { University, Faculty } from "./posts";
import {
    normalizeForSearch, attachDropdownKeyboardNav, navigateSmoothly, tokenDistance,
    SELECTED_UNIVERSITY_STORAGE_KEY as UNIVERSITY_STORAGE_KEY,
    SELECTED_FACULTY_STORAGE_KEY as FACULTY_STORAGE_KEY,
    saveValue, clearValue,
} from "./generics";
import { renderPostHocNotifyPrompt } from "./notifyEmail";

// Dwuetapowy picker uczelnia -> wydział, współdzielony przez /inne/ (landing2.ts, osobna strona)
// i kafelki na stronie głównej (landing3.ts - rozwijany w miejscu przyciskiem pod kafelkami, bez
// przeładowania strony). Najpierw uczelnia, potem wydział - to na poziomie wydziału studenci
// najczęściej się identyfikują, nie całej uczelni, więc wybór wydziału od razu przenosi na listę
// wykładowców TEGO wydziału (/<uczelnia>/<wydział>/), a nie na ogólną listę całej uczelni.


// Wydział-zbiornik z importu ogólnopolskiej bazy naukowców (patrz PLACEHOLDER_FACULTY_SLUG w
// import_naukowcy.py/assign_faculties.py) - czysto techniczny fallback, nigdy nie pokazujemy go
// jako wybieralną opcję (ten sam wzorzec co w addLecturer.ts) - inaczej, dla większości uczelni
// zaimportowanych z ogólnopolskiej bazy, byłby to JEDYNY (i najliczniejszy) "wydział" na liście.
const PLACEHOLDER_FACULTY_SLUG = "bez-wydzialu";

// Dopasowanie uczelni do wpisanej frazy: [kategoria, koszt] - 3: zgadza się skrót ("WUM", "PW");
// 2: fragment nazwy (jak dotąd); 1: z literówkami (koszt = ich liczba); 0: brak. Ten sam algorytm
// co match_score w pages/university_search.py (tam do wyłapania duplikatu przy dodawaniu).
function universityMatch(u: University, query: string): [number, number] {
    const q = normalizeForSearch(query);
    if (!q) return [0, 0];
    const aliases = (u.aliases ?? []).map(normalizeForSearch);
    if (aliases.some(a => a === q || a.startsWith(q + " ") || (q.length >= 2 && a.split(" ")[0] === q))) {
        return [3, 0];
    }
    const name = normalizeForSearch(u.name);
    if (u.initials && normalizeForSearch(u.initials) === q) return [2, 0];
    const words = [...name.split(/[\s-]+/), ...aliases.flatMap(a => a.split(" "))];
    // Krótkie frazy (do 3 znaków) tylko od początku słowa - inaczej "uj" trafia w "k-uj-awsko".
    if ((q.length >= 4 && name.includes(q)) || (q.length < 4 && words.some(w => w.startsWith(q)))) {
        return [2, 0];
    }
    let cost = 0;
    for (const queryToken of q.split(" ")) {
        const distances = words.map(w => tokenDistance(queryToken, w)).filter((d): d is number => d !== null);
        if (!distances.length) return [0, 0];
        cost += Math.min(...distances);
    }
    return [1, cost];
}

export function buildUniversityPicker(
    container: HTMLElement, universities: University[], restoreUniversitySlug: string | null,
): void {
    container.replaceChildren();

    const heading = document.createElement("p");
    heading.className = "landing-heading";
    heading.textContent = "Wybierz swoją uczelnię:";

    // ── Krok 1: uczelnia - rozwijana wyszukiwarka (jak wszystkie inne w serwisie). Po
    // wyborze pole wyszukiwania znika i w jego miejscu pojawia się "pigułka" z nazwą
    // uczelni oraz małym "×" cofającym wybór. ──
    const universityWrapper = document.createElement("div");
    universityWrapper.className = "landing-search-field";

    const universitySearchBox = document.createElement("div");
    universitySearchBox.className = "search-with-suggestions";

    const universityInput = document.createElement("input");
    universityInput.type = "text";
    // "landing-faculty-input" dokłada strzałkę w tle (patrz style.css) - ten sam sygnał "to
    // lista do wyboru, nie tylko wolny tekst" co przy wydziale niżej i przy przedmiocie
    // w formularzu opinii (mimo nazwy klasy, nie jest już specyficzna dla samego pola wydziału).
    universityInput.className = "landing-text-input landing-faculty-input";
    universityInput.placeholder = "Szukaj uczelni...";
    universityInput.autocomplete = "off";

    const universityDropdown = document.createElement("div");
    universityDropdown.className = "search-suggestions";

    // Komunikat po dodaniu nowej uczelni (patrz createUniversity niżej) - nowa uczelnia czeka
    // na moderację, więc TU nie ma dokąd nawigować (jej strony na razie dają 404, patrz
    // views.browse) - to samo zachowanie co przy wydziale (facultyFeedback niżej).
    const universityFeedback = document.createElement("p");
    universityFeedback.className = "landing-faculty-feedback";

    // Osobny kontener na widżet "zostaw e-mail" (patrz notifyEmail.ts) - pojawia się DOPIERO PO
    // faktycznym zapisaniu uczelni w bazie, nie z góry w formularzu. Osobny <div> (a nie np.
    // wstawienie prosto do universityFeedback) - żeby renderUniversityOptions niżej mogło go
    // wyczyścić razem z komunikatem, gdy ktoś zacznie szukać czegoś innego.
    const universityNotifyPromptContainer = document.createElement("div");

    // Propozycja istniejącej uczelni zamiast dodawania nowej (patrz _find_duplicate w
    // api/universities.py) - "to ta" wybiera ją jak z listy, "to inna" dodaje mimo to (force).
    const universityDuplicateContainer = document.createElement("div");

    function showDuplicateSuggestion(trimmedQuery: string, existing: University, kind: string | undefined, reason: string | undefined): void {
        const box = document.createElement("div");
        box.className = "university-duplicate";
        const text = document.createElement("p");
        text.append(
            kind === "part_of" ? "To chyba część uczelni, która już jest w serwisie: " : "Ta uczelnia chyba już jest w serwisie: ",
        );
        const strong = document.createElement("strong");
        strong.textContent = existing.name;
        text.append(strong);
        if (reason) text.append(document.createElement("br"), reason);
        if (kind === "part_of") {
            text.append(document.createElement("br"), "Wydział wybierzesz albo dodasz w następnym kroku, po wybraniu uczelni.");
        }
        const pick = document.createElement("button");
        pick.type = "button";
        pick.className = "university-duplicate-pick";
        pick.textContent = `Wybierz „${existing.name}”`;
        pick.addEventListener("click", () => {
            universityDuplicateContainer.replaceChildren();
            selectUniversity(universities.find(u => u.id === existing.id) ?? existing);
        });
        const addAnyway = document.createElement("button");
        addAnyway.type = "button";
        addAnyway.className = "university-duplicate-add";
        addAnyway.textContent = `To inna uczelnia - dodaj „${trimmedQuery}”`;
        addAnyway.addEventListener("click", () => createUniversity(trimmedQuery, addAnyway, true));
        box.append(text, pick, addAnyway);
        universityDuplicateContainer.replaceChildren(box);
    }

    async function createUniversity(trimmedQuery: string, createItem: HTMLButtonElement, force = false): Promise<void> {
        createItem.disabled = true;
        createItem.textContent = force ? "Dodawanie..." : "Sprawdzam, czy tej uczelni nie ma już w serwisie...";

        const res = await api.createUniversity(trimmedQuery, force);

        if (res.ok && res.data.duplicate_of) {
            universityInput.value = "";
            universityDropdown.classList.remove("open");
            universityFeedback.textContent = "";
            showDuplicateSuggestion(trimmedQuery, res.data.duplicate_of, res.data.kind, res.data.reason);
            return;
        }
        universityDuplicateContainer.replaceChildren();

        if (res.ok && res.data.id !== undefined) {
            universityInput.value = "";
            universityDropdown.classList.remove("open");
            // Nowo dodana uczelnia czeka na zatwierdzenie (patrz UniversityListView.create) -
            // nie pojawia się jeszcze na liście ani pod własnym adresem, stąd brak nawigacji
            // dalej do kroku 2 (wybór wydziału).
            universityFeedback.textContent =
                `Dziękujemy! Uczelnia "${res.data.name}" czeka na zatwierdzenie przez administratora.`;
            universityNotifyPromptContainer.replaceChildren(
                renderPostHocNotifyPrompt([{ model: "university", pk: res.data.id }]),
            );
        } else {
            createItem.disabled = false;
            createItem.textContent = force ? `To inna uczelnia - dodaj „${trimmedQuery}”` : `+ Dodaj uczelnię: "${trimmedQuery}"`;
            universityDropdown.classList.remove("open");
            universityFeedback.textContent = `Błąd: ${res.ok ? "nieoczekiwana odpowiedź serwera" : res.error}`;
        }
    }

    // Pusta fraza pokazuje wszystkie uczelnie, najaktywniejsze pierwsze (przychodzą z API
    // posortowane wg liczby opinii - patrz UniversityListView.get_queryset) - lista jest
    // przewijana (patrz .search-suggestions w style.css), więc nie trzeba jej ucinać (ten sam
    // wzorzec co przy wydziale niżej).
    function renderUniversityOptions(query: string): void {
        universityDropdown.replaceChildren();
        // NIE czyścimy universityFeedback/universityNotifyPromptContainer tutaj - to się
        // odpalałoby przy każdym wciśniętym klawiszu, chowając widżet "zostaw e-mail" (patrz
        // notifyEmail.ts) w trakcie, gdy ktoś próbuje w niego coś wpisać. Nadpisuje się
        // naturalnie dopiero przy faktycznym dodaniu kolejnej uczelni (patrz createUniversity)
        // albo jawnym cofnięciu wyboru (patrz universityPillClear niżej).
        const normalizedQuery = normalizeForSearch(query);

        // Najpierw trafienia po skrócie, potem zwykłe po fragmencie nazwy, na końcu z literówkami
        // (mniej literówek wyżej); w każdej grupie zostaje kolejność z API (najaktywniejsze
        // pierwsze) - sort() w JS jest stabilny.
        const matches = normalizedQuery
            ? universities
                .map(u => ({ u, score: universityMatch(u, normalizedQuery) }))
                .filter(m => m.score[0] > 0)
                .sort((a, b) => b.score[0] - a.score[0] || a.score[1] - b.score[1])
                .map(m => m.u)
            : universities;

        matches.forEach(u => {
            const item = document.createElement("button");
            item.type = "button";
            item.className = "search-suggestion-item";
            // Sama nazwa, bez skrótu w nawiasie (na prośbę 2026-10-23) - po skrócie dalej się
            // wyszukuje (patrz universityMatch wyżej), tylko go nie wyświetlamy.
            item.textContent = u.name;
            item.addEventListener("click", () => selectUniversity(u));
            universityDropdown.appendChild(item);
        });

        // "+ Dodaj uczelnię" - tylko gdy coś wpisano i nie ma dokładnego dopasowania (ten sam
        // wzorzec co przy wydziale/przedmiocie/wykładowcy - patrz np. createFaculty niżej).
        const trimmedQuery = query.trim();
        const exactMatch = universities.some(u => normalizeForSearch(u.name) === normalizedQuery);
        if (trimmedQuery && !exactMatch) {
            const createItem = document.createElement("button");
            createItem.type = "button";
            createItem.className = "search-suggestion-item search-suggestion-create";
            createItem.textContent = matches.length
                ? `+ Mojej uczelni nie ma na liście - dodaj „${trimmedQuery}”`
                : `+ Dodaj uczelnię: "${trimmedQuery}"`;
            createItem.addEventListener("click", () => createUniversity(trimmedQuery, createItem));
            universityDropdown.appendChild(createItem);
        }

        universityDropdown.classList.toggle("open", universityDropdown.children.length > 0);
    }

    universityInput.addEventListener("input", () => renderUniversityOptions(universityInput.value));
    universityInput.addEventListener("focus", () => renderUniversityOptions(universityInput.value));
    attachDropdownKeyboardNav(universityInput, universityDropdown);

    universitySearchBox.append(universityInput, universityDropdown);

    const universityPill = document.createElement("div");
    universityPill.className = "landing-university-pill";
    universityPill.hidden = true;

    const universityPillName = document.createElement("span");
    universityPillName.className = "landing-university-pill-name";

    const universityPillClear = document.createElement("button");
    universityPillClear.type = "button";
    universityPillClear.className = "landing-university-pill-clear";
    universityPillClear.setAttribute("aria-label", "Zmień uczelnię");
    universityPillClear.textContent = "×";

    universityPill.append(universityPillName, universityPillClear);

    universityWrapper.append(
        universitySearchBox, universityPill, universityFeedback, universityDuplicateContainer, universityNotifyPromptContainer,
    );

    // ── Krok 2: wydział tej uczelni - w tym samym miejscu co byłby "pod" krokiem 1, po prostu
    // odkryty (hidden), a nie wstawiony przez zastąpienie całej sekcji - dzięki temu wybór
    // uczelni nie powoduje skoku/zmiany wysokości reszty strony. Wybór wydziału od razu
    // przenosi na jego listę wykładowców (patrz goToFaculty niżej) - nie ma tu (celowo) też
    // wyszukiwarki konkretnego wykładowcy, bo lista docelowa ma już swoją. ──
    const facultySection = document.createElement("div");
    facultySection.className = "landing-faculty-section";
    facultySection.hidden = true;

    const facultyWrapper = document.createElement("div");
    facultyWrapper.className = "search-with-suggestions landing-search-field";

    const facultyInput = document.createElement("input");
    facultyInput.type = "text";
    // "landing-faculty-input" dokłada strzałkę w tle (patrz style.css) - ten sam sygnał
    // "to lista do wyboru" co przy przedmiocie w formularzu opinii (renderSubjectPicker
    // w comments.ts). Tylko przy wydziale (nie uczelni) - kliknięcie od razu pokazuje coś do
    // wyboru, bez potrzeby wpisywania (część uczelni, np. UW, ma teraz kilkadziesiąt wydziałów -
    // patrz renderFacultyOptions niżej: sortujemy po liczbie wykładowców i sygnalizujemy, gdy
    // lista jest ucięta, żeby nie wyglądało to na "to wszystko, co jest").
    facultyInput.className = "landing-text-input landing-faculty-input";
    facultyInput.placeholder = "Wybierz wydział...";
    facultyInput.autocomplete = "off";

    const facultyDropdown = document.createElement("div");
    facultyDropdown.className = "search-suggestions";

    // Ukryty na razie (patrz niżej "Wszystkie wydziały" w liście rozwijanej, który go
    // zastępuje) - element zostaje zdefiniowany i wciąż zasilany href-em w revealFacultyStep,
    // celowo tylko nie jest już dopisywany do DOM (facultySection.append niżej), żeby łatwo
    // było go przywrócić.
    const browseAllLink = document.createElement("a");
    browseAllLink.className = "landing-browse-link";
    browseAllLink.textContent = "Przeglądaj wszystkich wykładowców tej uczelni";

    // Komunikat po dodaniu nowego wydziału (patrz createFaculty niżej) - nowy wydział czeka na
    // moderację, więc TU nie ma dokąd nawigować (jego /<uczelnia>/<wydział>/ na razie da 404,
    // patrz views.browse) - zamiast tego zostaje info + odsyłacz do browseAllLink wyżej.
    const facultyFeedback = document.createElement("p");
    facultyFeedback.className = "landing-faculty-feedback";

    // Patrz analogiczny universityNotifyPromptContainer wyżej.
    const facultyNotifyPromptContainer = document.createElement("div");

    let faculties: Faculty[] = [];
    let selectedUniversitySlug: string | null = null;

    function goToFaculty(universitySlug: string, faculty: Faculty): void {
        saveValue(FACULTY_STORAGE_KEY, faculty.slug);
        navigateSmoothly(`/${universitySlug}/${faculty.slug}/`);
    }

    async function createFaculty(trimmedQuery: string, createItem: HTMLButtonElement): Promise<void> {
        if (!selectedUniversitySlug) return;
        const universitySlug = selectedUniversitySlug;

        createItem.disabled = true;
        createItem.textContent = "Dodawanie...";

        const res = await api.createFaculty(trimmedQuery, universitySlug);

        if (res.ok) {
            facultyInput.value = "";
            closeFacultyDropdown();
            // Nowo dodany wydział czeka na zatwierdzenie (patrz FacultyListView.create) - nie
            // pojawia się jeszcze na liście ani pod własnym adresem, stąd brak nawigacji.
            facultyFeedback.textContent =
                `Dziękujemy! Wydział "${res.data.name}" czeka na zatwierdzenie przez administratora. ` +
                `W międzyczasie możesz przeglądać wszystkich wykładowców tej uczelni.`;
            facultyNotifyPromptContainer.replaceChildren(
                renderPostHocNotifyPrompt([{ model: "faculty", pk: res.data.id }]),
            );
        } else {
            createItem.disabled = false;
            createItem.textContent = `+ Dodaj wydział: "${trimmedQuery}"`;
            closeFacultyDropdown();
            facultyFeedback.textContent = `Błąd: ${res.error}`;
        }
    }

    function closeFacultyDropdown(): void {
        facultyDropdown.classList.remove("open");
    }

    // Pusta fraza pokazuje WSZYSTKIE wydziały (nie tylko dopasowania) - to jedyny sposób
    // wyboru, więc lista musi być widoczna od razu po kliknięciu w pole, bez wpisywania
    // czegokolwiek. Lista jest przewijana (patrz .search-suggestions w style.css), więc nawet
    // przy kilkudziesięciu wydziałach (np. UW) nie trzeba jej ucinać.
    function renderFacultyOptions(query: string): void {
        facultyDropdown.replaceChildren();
        // Patrz analogiczna notka w renderUniversityOptions wyżej - nie chowamy
        // facultyFeedback/facultyNotifyPromptContainer przy samym wpisywaniu.
        const normalizedQuery = normalizeForSearch(query);

        // Wydziały przychodzą z API posortowane malejąco wg liczby wykładowców (patrz
        // FacultyListView.get_queryset) - filter() zachowuje tę kolejność, więc podpowiedzi
        // też są w tej kolejności: najpierw wydziały, na których faktycznie jest kogo znaleźć.
        const matches = normalizedQuery
            ? faculties.filter(f => normalizeForSearch(f.name).includes(normalizedQuery))
            : faculties;

        matches.forEach(f => {
            const item = document.createElement("button");
            item.type = "button";
            item.className = "search-suggestion-item";
            item.textContent = f.name;
            item.addEventListener("click", () => {
                if (selectedUniversitySlug) goToFaculty(selectedUniversitySlug, f);
            });
            facultyDropdown.appendChild(item);
        });

        // "Cała uczelnia" - poniżej WSZYSTKICH konkretnych wydziałów (żeby nie przesłaniała tego,
        // czego ktoś tu szuka w pierwszej kolejności), ale WCIĄŻ przed "+ Dodaj wydział" niżej -
        // to jest jawna, równorzędna opcja (patrz rozmowa z użytkownikiem, sierpień 2026: część
        // wydziałów nie ma jeszcze ani jednej opinii, więc przeglądanie całej uczelni na raz jest
        // realnym, nie awaryjnym wyborem), a "+ Dodaj wydział" to zawsze ostatnia, "ucieczkowa"
        // pozycja.
        const wholeUniversityItem = document.createElement("button");
        wholeUniversityItem.type = "button";
        wholeUniversityItem.className = "search-suggestion-item search-suggestion-all";
        wholeUniversityItem.textContent = "Cała uczelnia";
        wholeUniversityItem.addEventListener("click", () => {
            if (selectedUniversitySlug) navigateSmoothly(`/${selectedUniversitySlug}/`);
        });
        facultyDropdown.appendChild(wholeUniversityItem);

        // "+ Dodaj wydział" - tylko gdy coś wpisano i nie ma dokładnego dopasowania (ten sam
        // wzorzec co przy przedmiotach/wykładowcach w reszcie serwisu - patrz np.
        // renderSubjectPicker w comments.ts).
        const trimmedQuery = query.trim();
        const exactMatch = faculties.some(f => normalizeForSearch(f.name) === normalizedQuery);
        if (trimmedQuery && !exactMatch) {
            const createItem = document.createElement("button");
            createItem.type = "button";
            createItem.className = "search-suggestion-item search-suggestion-create";
            createItem.textContent = `+ Dodaj wydział: "${trimmedQuery}"`;
            createItem.addEventListener("click", () => createFaculty(trimmedQuery, createItem));
            facultyDropdown.appendChild(createItem);
        }

        facultyDropdown.classList.toggle("open", facultyDropdown.children.length > 0);
    }

    facultyInput.addEventListener("input", () => renderFacultyOptions(facultyInput.value));
    facultyInput.addEventListener("focus", () => renderFacultyOptions(facultyInput.value));
    attachDropdownKeyboardNav(facultyInput, facultyDropdown);

    facultyWrapper.append(facultyInput, facultyDropdown);
    facultySection.append(facultyWrapper, facultyFeedback, facultyNotifyPromptContainer);

    document.addEventListener("click", (event) => {
        if (!universityWrapper.contains(event.target as Node)) {
            universityDropdown.classList.remove("open");
        }
        if (!facultyWrapper.contains(event.target as Node)) {
            facultyDropdown.classList.remove("open");
        }
    });

    // Odkrywa krok 2 i ładuje wydziały danej uczelni - wspólne dla świeżego wyboru uczelni
    // (selectUniversity) i przywracania zapamiętanej uczelni przy starcie (init/buildLandingUI).
    function revealFacultyStep(u: University): void {
        selectedUniversitySlug = u.slug;
        heading.hidden = true;

        universityPillName.textContent = u.name;
        universityPill.hidden = false;
        universitySearchBox.hidden = true;
        universityInput.value = "";
        universityDropdown.replaceChildren();
        universityDropdown.classList.remove("open");

        browseAllLink.href = `/${u.slug}/`;

        facultySection.hidden = false;
        facultyInput.value = "";
        facultyDropdown.replaceChildren();
        facultyFeedback.textContent = "";
        facultyNotifyPromptContainer.replaceChildren();
        faculties = [];

        api.getFaculties(u.slug).then(res => {
            if (!res.ok) return;
            // Zbiornik z importu (patrz PLACEHOLDER_FACULTY_SLUG wyżej) nigdy nie jest
            // wybieralną opcją - dla większości uczelni to i tak jedyny/najliczniejszy "wydział",
            // więc bez tego byłby pierwszym (albo jedynym) wynikiem.
            faculties = res.data.faculties.filter(f => f.slug !== PLACEHOLDER_FACULTY_SLUG);
            // no_faculties (University.no_faculties, patrz research w scrape_wydzialy_openai.py)
            // to potwierdzona flaga "ta uczelnia nie ma struktury wydziałowej" - sprawdzamy ją
            // NIEZALEŻNIE od faculties.length, tak samo jak w addLecturer.ts, żeby zachowanie nie
            // zależało wyłącznie od przypadkowej liczby wierszy Faculty w bazie.
            if (res.data.no_faculties || faculties.length === 0) {
                // Ta uczelnia nie ma żadnego prawdziwego wydziału (tylko zbiornik) - pokazywanie
                // pustego pickera nie ma sensu, od razu przechodzimy na listę całej uczelni
                // (dokładnie to, co i tak robi browseAllLink wyżej).
                navigateSmoothly(browseAllLink.href);
                return;
            }
            // Jeden prawdziwy wydział na tej uczelni - nie ma czego wybierać, przechodzimy od razu.
            if (faculties.length === 1) {
                goToFaculty(u.slug, faculties[0]);
                return;
            }
            renderFacultyOptions("");
        });
    }

    function selectUniversity(u: University): void {
        saveValue(UNIVERSITY_STORAGE_KEY, u.slug);
        // Nowo wybrana uczelnia unieważnia poprzednio zapamiętany wydział (należał do innej
        // uczelni) - inaczej init() przy kolejnej wizycie próbowałby przenieść na wydział
        // spod zupełnie innej uczelni.
        clearValue(FACULTY_STORAGE_KEY);
        revealFacultyStep(u);
    }

    universityPillClear.addEventListener("click", () => {
        clearValue(UNIVERSITY_STORAGE_KEY);
        clearValue(FACULTY_STORAGE_KEY);
        selectedUniversitySlug = null;
        heading.hidden = false;

        universityPill.hidden = true;
        universitySearchBox.hidden = false;
        universityInput.value = "";
        universityFeedback.textContent = "";
        universityNotifyPromptContainer.replaceChildren();
        universityDuplicateContainer.replaceChildren();
        universityInput.focus();

        facultySection.hidden = true;
        facultyInput.value = "";
        facultyDropdown.replaceChildren();
        facultyFeedback.textContent = "";
        facultyNotifyPromptContainer.replaceChildren();
        faculties = [];
    });

    container.append(heading, universityWrapper, facultySection);

    // Uczelnia zapamiętana (ale nie oba na raz - ten przypadek załatwia init() wcześniej,
    // przenosząc na listę zanim UI się w ogóle zbuduje) - pokazujemy od razu krok 2, bez
    // migania krokiem 1.
    if (restoreUniversitySlug) {
        const saved = universities.find(u => u.slug === restoreUniversitySlug);
        if (saved) {
            revealFacultyStep(saved);
        }
    }
}

// Ładuje listę uczelni i buduje picker w container - z komunikatem "Ładowanie..." w międzyczasie.
export async function mountUniversityPicker(container: HTMLElement, restoreUniversitySlug: string | null): Promise<void> {
    container.textContent = "Ładowanie...";
    const res = await api.getUniversities();

    if (!res.ok) {
        container.textContent = `Błąd: ${res.error}`;
        return;
    }

    buildUniversityPicker(container, res.data.universities, restoreUniversitySlug);
}
